import { Resource, Suggestion, epochISOString } from 'idea-toolbox';

import { EventSpotAttached } from './eventSpot.model';
import { Speaker } from './speaker.model';
import { ApprovedType, MealTypes } from './meal.model';

/**
 * The default versions of the legal documents of the platform, i.e. the ones of the default documents (translations).
 * They apply until the admins edit a document and ask the users to accept it again (see `LegalDocument`).
 */
export const LEGAL_DOCUMENTS_VERSIONS: LegalDocumentsVersions = {
  termsAndConditions: '2024-10-08',
  privacyPolicy: '2026-09-19'
};

export class User extends Resource {
  /**
   * The ID of the user; it's the concatenation of the key of the auth service and the username there.
   */
  userId: string;
  /**
   * Timestamp of when the user signed-up.
   */
  createdAt: epochISOString;
  /**
   * Timestamp of the last time the user did something.
   */
  lastSeenAt: epochISOString;
  /**
   * The service used to authenticate.
   */
  authService: AuthServices;
  /**
   * The first name of the user.
   */
  firstName: string;
  /**
   * The last name of the user.
   */
  lastName: string;
  /**
   * Email address for notifications (!== account email address).
   * Note: if bounced, it will finish in the black list for a while (see `sesNotifications.ts`).
   */
  email: string;
  /**
   * The URL to the user's avatar.
   */
  avatarURL: string;

  /**
   * ESN section code in ESN Accounts.
   * Set only for `AuthService.ESN_ACCOUNTS`.
   */
  sectionCode?: string;
  /**
   * ESN country.
   * Set only for `AuthService.ESN_ACCOUNTS`.
   */
  sectionCountry?: string;
  /**
   * ESN section name.
   * Set only for `AuthService.ESN_ACCOUNTS`.
   */
  sectionName?: string;
  /**
   * The number of the user's ESNcard; it's verified against the ESNcard API when the registration is submitted
   * and whenever it's changed afterwards.
   * Set only for `AuthService.ESN_ACCOUNTS`.
   */
  esnCard?: string;
  /**
   * The user's birth date.
   */
  birthDate: epochISOString;
  /**
   * The number of the user's ID document or passport; requested in the registration form.
   */
  idDocumentNumber?: string;
  /**
   * When the user's ID document or passport was issued; requested in the registration form.
   */
  idDocumentIssuedAt?: epochISOString;
  /**
   * Until when the user's ID document or passport is valid; requested in the registration form.
   */
  idDocumentValidUntil?: epochISOString;
  /**
   * The permissions of the user on the app.
   */
  permissions: UserPermissions;
  /**
   * A custom block containing custom sections and fields for the registration form.
   */
  registrationForm: Record<string, any>;
  /**
   * The timestamp when the registration was submitted (if it was).
   * If set, the registration is considered completed.
   */
  registrationAt?: epochISOString;
  /**
   * The spot assigned for the event, if any.
   */
  spot?: EventSpotAttached;
  /**
   * Whether the user is part of ESN International (based on the roles on ESN Accounts); ESN Accounts users only.
   */
  isESNInternational: boolean;
  /**
   * The roles of the user on ESN Accounts (ESN Accounts users only); refreshed at every sign-in.
   */
  esnRoles: string[];
  /**
   * The user's social media links, if any.
   */
  socialMedia: SocialMedia;
  /**
   * The list of contests (IDs) the user voted in.
   */
  votedInContests: string[];
  // @todo add this to registration field.
  /**
   * The user's meal preference.
   */
  mealType: MealTypes;
  /**
   * The user's meal tickets and their status.
   */
  mealTickets: { [mealId: string]: MealTicket };
  /**
   * The record of the user's acceptance of the legal documents (GDPR art. 7.1); set by the back-end only.
   */
  legalAcceptance?: LegalAcceptance;
  /**
   * The record of the consents given when submitting the event registration; set by the back-end only.
   */
  registrationConsents?: RegistrationConsents;

  load(x: any): void {
    super.load(x);
    this.userId = this.clean(x.userId, String);
    this.createdAt = this.clean(x.createdAt, t => new Date(t).toISOString(), new Date().toISOString());
    this.lastSeenAt = new Date().toISOString();
    this.authService = this.clean(x.authService, String);
    this.firstName = this.clean(x.firstName, String);
    this.lastName = this.clean(x.lastName, String);
    this.email = this.clean(x.email, String);
    this.avatarURL = this.clean(x.avatarURL, String);
    this.birthDate = this.clean(x.birthDate, t => new Date(t).toISOString());
    if (x.idDocumentNumber) this.idDocumentNumber = this.clean(x.idDocumentNumber, n => String(n).trim());
    if (x.idDocumentIssuedAt)
      this.idDocumentIssuedAt = this.clean(x.idDocumentIssuedAt, t => new Date(t).toISOString());
    if (x.idDocumentValidUntil)
      this.idDocumentValidUntil = this.clean(x.idDocumentValidUntil, t => new Date(t).toISOString());

    if (this.authService === AuthServices.ESN_ACCOUNTS) {
      this.sectionCode = this.clean(x.sectionCode, String);
      this.sectionCountry = this.clean(x.sectionCountry, String);
      this.sectionName = this.clean(x.sectionName, String);
      if (x.esnCard) this.esnCard = User.normalizeESNcard(x.esnCard);
      this.isESNInternational = Boolean(x.isESNInternational);
      this.esnRoles = this.cleanArray(x.esnRoles, String);
    }

    this.permissions = new UserPermissions(x.permissions);

    this.registrationForm = x.registrationForm ?? {};
    if (x.registrationAt) this.registrationAt = this.clean(x.registrationAt, t => new Date(t).toISOString());
    if (x.spot) this.spot = new EventSpotAttached(x.spot);
    this.socialMedia = {};
    if (x.socialMedia?.instagram) this.socialMedia.instagram = this.clean(x.socialMedia.instagram, String);
    if (x.socialMedia?.linkedIn) this.socialMedia.linkedIn = this.clean(x.socialMedia.linkedIn, String);
    if (x.socialMedia?.twitter) this.socialMedia.twitter = this.clean(x.socialMedia.twitter, String);

    this.votedInContests = this.cleanArray(x.votedInContests, String);

    this.mealType = x.mealType;
    if (!x.mealTickets) this.mealTickets = {};
    else this.mealTickets = x.mealTickets;

    if (x.legalAcceptance) this.legalAcceptance = loadLegalAcceptance(x.legalAcceptance);
    if (x.registrationConsents) this.registrationConsents = loadRegistrationConsents(x.registrationConsents);
  }

  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
    this.userId = safeData.userId;
    this.createdAt = safeData.createdAt;
    this.lastSeenAt = new Date().toISOString();
    this.authService = safeData.authService;

    if (this.authService === AuthServices.ESN_ACCOUNTS) {
      this.sectionCode = safeData.sectionCode;
      this.sectionCountry = safeData.sectionCountry;
      this.sectionName = safeData.sectionName;
      this.birthDate = safeData.birthDate;
      this.isESNInternational = safeData.isESNInternational;
      this.esnRoles = safeData.esnRoles;
    }

    this.permissions = safeData.permissions;

    if (safeData.registrationForm) this.registrationForm = safeData.registrationForm;
    if (safeData.registrationAt) this.registrationAt = safeData.registrationAt;
    if (safeData.spot) this.spot = safeData.spot;

    this.votedInContests = safeData.votedInContests;
    this.mealType = safeData.mealType;

    if (safeData.legalAcceptance) this.legalAcceptance = safeData.legalAcceptance;
    else delete this.legalAcceptance;
    if (safeData.registrationConsents) this.registrationConsents = safeData.registrationConsents;
    else delete this.registrationConsents;
  }

  validate(): string[] {
    const e = super.validate();
    if (this.iE(this.firstName)) e.push('firstName');
    if (this.iE(this.lastName)) e.push('lastName');
    if (this.iE(this.email, 'email')) e.push('email');
    return e;
  }

  /**
   * Normalize an ESNcard number, to store it and to look it up in the ESNcard API.
   */
  static normalizeESNcard(esnCard: string): string {
    return String(esnCard).replace(/\s/g, '').toUpperCase();
  }
  /**
   * Whether the string looks like an ESNcard number; the actual validity is verified against the ESNcard API.
   */
  static isESNcardFormatValid(esnCard: string): boolean {
    return /^[0-9A-Z]{6,20}$/.test(esnCard);
  }

  /**
   * Get the original user ID in the AuthService.
   */
  getAuthServiceUserId(): string {
    return this.userId.slice(this.authService.concat('_').length);
  }

  /**
   * Get the full name of the user.
   */
  getName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  /**
   * Get a string representing the ESN section and country of the user.
   */
  getSectionCountry(): string {
    return [this.sectionCountry, this.sectionName].filter(x => x).join(' - ');
  }

  /**
   * Whether the user is considered an external guest.
   */
  isExternal(): boolean {
    return this.authService !== AuthServices.ESN_ACCOUNTS;
  }

  isSpeaker(speaker: Speaker): boolean {
    return speaker.speakerId === this.userId;
  }

  isGalaxyInfoValid(): boolean {
    if (this.isExternal()) return true;
    return this.sectionName !== 'Unknown' && this.sectionCountry !== 'Unknown';
  }

  /**
   * Whether the user belongs to a known ESN section.
   */
  hasKnownSection(): boolean {
    return !this.isExternal() && !!this.sectionCode && this.isGalaxyInfoValid();
  }

  /**
   * Map the user into a Suggestion data structure.
   */
  mapIntoSuggestion(): Suggestion {
    return new Suggestion({
      value: this.userId,
      name: this.getName(),
      description: this.email,
      category1: this.sectionCountry,
      category2: this.sectionName
    });
  }

  isMealTicketApproved(mealId: string): boolean {
    return !!this.mealTickets?.[mealId]?.approvedAt;
  }

  /**
   * Whether the user has accepted the current versions of the legal documents.
   */
  hasAcceptedCurrentLegalDocuments(currentVersions: LegalDocumentsVersions): boolean {
    return (
      !!this.legalAcceptance?.acceptedAt &&
      this.legalAcceptance.termsAndConditionsVersion === currentVersions.termsAndConditions &&
      this.legalAcceptance.privacyPolicyVersion === currentVersions.privacyPolicy
    );
  }
}

/**
 * The authentication services for the platform.
 */
export enum AuthServices {
  ESN_ACCOUNTS = 'EA', // aka Galaxy
  COGNITO = 'CO'
}

/**
 * The pseudo-country used to assign spots to the ESN International delegation (not tied to any ESN country).
 */
export const ESN_INTERNATIONAL = 'ESN International';

/**
 * The permissions a user can have.
 */
export class UserPermissions {
  /**
   * Whether the user can manage spots assigned to their ESN country.
   */
  isCountryLeader: boolean;
  /**
   * Whether the user can manage spots assigned to the ESN International delegation.
   */
  isESNInternationalLeader: boolean;
  /**
   * Whether the user has administrative permissions on the registrations.
   */
  canManageRegistrations: boolean;
  /**
   * Whether the user havs administrative permissions on the contents (speakers, sessions, etc.).
   */
  canManageContents: boolean;
  /**
   * Whether the user has staff permissions.
   */
  isStaff: boolean;
  /**
   * Whether the user has maximum permissions over the app.
   * If this is true, all other permissions are.
   */
  protected _isAdmin: boolean;
  get isAdmin(): boolean {
    return this._isAdmin;
  }
  set isAdmin(isAdmin: boolean) {
    this._isAdmin = isAdmin;
    if (isAdmin) {
      this.isCountryLeader = true;
      this.isESNInternationalLeader = true;
      this.canManageRegistrations = true;
      this.canManageContents = true;
      this.isStaff = true;
    }
  }

  constructor(x?: any) {
    x = x || {};
    this.isCountryLeader = Boolean(x.isCountryLeader);
    this.isESNInternationalLeader = Boolean(x.isESNInternationalLeader);
    this.canManageRegistrations = Boolean(x.canManageRegistrations);
    this.canManageContents = Boolean(x.canManageContents);
    this.isStaff = Boolean(x.isStaff);
    // as last, to change any other attribute in case it's `true`
    this.isAdmin = Boolean(x._isAdmin);
  }
}

export class UserFavoriteSession extends Resource {
  userId: string;
  sessionId: string;

  load(x: any): void {
    super.load(x);
    this.userId = this.clean(x.userId, String);
    this.sessionId = this.clean(x.sessionId, String);
  }
}

export interface SocialMedia {
  instagram?: string;
  linkedIn?: string;
  twitter?: string;
}

/**
 * The versions of the legal documents of the platform.
 */
export interface LegalDocumentsVersions {
  /**
   * The version of the Terms and Conditions.
   */
  termsAndConditions: string;
  /**
   * The version of the Privacy Policy.
   */
  privacyPolicy: string;
}

/**
 * The proof that a user accepted the Terms and Conditions and was shown the Privacy Policy.
 */
export interface LegalAcceptance {
  /**
   * The version of the Terms and Conditions accepted.
   */
  termsAndConditionsVersion: string;
  /**
   * The version of the Privacy Policy acknowledged.
   */
  privacyPolicyVersion: string;
  /**
   * When the acceptance was recorded (server time).
   */
  acceptedAt: epochISOString;
}

/**
 * The proof of the consents given by a user when submitting the event registration.
 */
export interface RegistrationConsents {
  /**
   * When the user accepted the event's Terms and Conditions.
   */
  termsAndConditionsAt: epochISOString;
  /**
   * When the user accepted the event's Code of Conduct.
   */
  codeOfConductAt: epochISOString;
  /**
   * When the user gave explicit consent (GDPR art. 9.2.a) to the processing of dietary requirements.
   * Mandatory to submit a registration; not set only for the registrations submitted before it became so.
   */
  dietaryDataAt?: epochISOString;
}

/**
 * Load and clean a legal acceptance record.
 */
const loadLegalAcceptance = (x: any): LegalAcceptance => ({
  termsAndConditionsVersion: String(x.termsAndConditionsVersion),
  privacyPolicyVersion: String(x.privacyPolicyVersion),
  acceptedAt: new Date(x.acceptedAt).toISOString()
});

/**
 * Load and clean a record of registration consents.
 */
const loadRegistrationConsents = (x: any): RegistrationConsents => ({
  termsAndConditionsAt: new Date(x.termsAndConditionsAt).toISOString(),
  codeOfConductAt: new Date(x.codeOfConductAt).toISOString(),
  ...(x.dietaryDataAt ? { dietaryDataAt: new Date(x.dietaryDataAt).toISOString() } : {})
});

export interface MealTicket {
  approvedAt?: epochISOString;
  approvedBy?: string;
  approvedType?: ApprovedType;
}

/**
 * The minimal, public representation of a user of an ESN section who registered to the event.
 */
export class SectionMember extends Resource {
  /**
   * The ID of the user.
   */
  userId: string;
  /**
   * The full name of the user.
   */
  name: string;
  /**
   * Whether the user completed (submitted) the registration to the event.
   */
  isRegistered: boolean;

  load(x: any): void {
    super.load(x);
    this.userId = this.clean(x.userId, String);
    this.name = x.name ? this.clean(x.name, String) : [x.firstName, x.lastName].filter(n => n).join(' ');
    this.isRegistered = x.isRegistered ?? !!x.registrationAt;
  }
}
